import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateBranchOwnerThunk,
  updateAdminPrimaryBranchThunk,
  updateAdminSecondaryBranchesThunk,
  getAllBranchesThunk,
} from "../../../store/thunks/branchThunks";
import { fetchAdminsThunk } from "../../../store/thunks/adminThunks";
import {
  UserIcon,
  CheckIcon,
  PencilIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/outline";
import Dropdown from "../../common/Dropdown";

const BranchOwnershipManagement = ({ branch }) => {
  const dispatch = useDispatch();
  const { admins, loading: adminLoading } = useSelector((state) => state.admin);
  const { allBranches, loading: branchLoading } = useSelector(
    (state) => state.branch
  );
  const { isRootAdmin } = useSelector((state) => state.auth);

  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState(branch?.ownerId || "");
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [selectedPrimaryBranch, setSelectedPrimaryBranch] = useState(null);
  const [selectedSecondaryBranches, setSelectedSecondaryBranches] = useState(
    []
  );
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    if (branch?.customerId) {
      dispatch(fetchAdminsThunk());
      dispatch(getAllBranchesThunk());
    }
  }, [dispatch, branch?.customerId]);

  // Filter admins for same customer
  const customerAdmins = admins.filter(
    (admin) => admin.customerId === branch?.customerId && !admin.isBlocked
  );

  // Transform branches for dropdown format
  const branchOptions = allBranches.map((b) => ({
    _id: b.code, // Use code as _id
    name: `${b.name} (${b.code})`,
  }));

  // Transform admins for dropdown format
  const adminOptions = customerAdmins.map((admin) => ({
    _id: admin._id,
    name: `${admin.name} - ${admin.email} (${admin.role})`,
  }));

  // Find current owner
  const currentOwner = customerAdmins.find(
    (admin) => admin._id === branch?.ownerId
  );

  const handleUpdateOwner = async () => {
    if (!selectedOwnerId || selectedOwnerId === branch?.ownerId) {
      setIsEditingOwner(false);
      return;
    }

    try {
      await dispatch(
        updateBranchOwnerThunk({
          branchId: branch._id,
          ownerId: selectedOwnerId,
        })
      ).unwrap();

      setShowSuccessMessage(true);
      setIsEditingOwner(false);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error("Failed to update branch owner:", error);
    }
  };

  const handleEditAdminAccess = (admin) => {
    setEditingAdminId(admin._id);

    // Set primary branch dropdown selection
    if (admin.branchAccess?.primaryBranch) {
      const primaryBranch = branchOptions.find(
        (b) => b._id === admin.branchAccess.primaryBranch
      );
      setSelectedPrimaryBranch(primaryBranch || null);
    } else {
      setSelectedPrimaryBranch(null);
    }

    // Set secondary branches dropdown selection
    const secondaryBranches =
      admin.branchAccess?.secondaryBranches?.map((code) =>
        branchOptions.find((b) => b._id === code)
      ) || [];
    setSelectedSecondaryBranches(secondaryBranches.filter(Boolean));
  };

  const handleUpdatePrimaryBranch = async (adminId) => {
    if (!selectedPrimaryBranch) {
      alert("Please select a primary branch");
      return;
    }

    try {
      await dispatch(
        updateAdminPrimaryBranchThunk({
          adminId,
          branchCode: selectedPrimaryBranch._id, // _id is the branch code
        })
      ).unwrap();

      setShowSuccessMessage(true);
      setEditingAdminId(null);
      setSelectedPrimaryBranch(null);
      setSelectedSecondaryBranches([]);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      dispatch(fetchAdminsThunk());
    } catch (error) {
      console.error("Failed to update primary branch:", error);
    }
  };

  const handleUpdateSecondaryBranches = async (adminId) => {
    try {
      const branchCodes = selectedSecondaryBranches.map((b) => b._id);

      await dispatch(
        updateAdminSecondaryBranchesThunk({
          adminId,
          branchCodes,
        })
      ).unwrap();

      setShowSuccessMessage(true);
      setEditingAdminId(null);
      setSelectedPrimaryBranch(null);
      setSelectedSecondaryBranches([]);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      dispatch(fetchAdminsThunk());
    } catch (error) {
      console.error("Failed to update secondary branches:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingAdminId(null);
    setSelectedPrimaryBranch(null);
    setSelectedSecondaryBranches([]);
  };

  if (!isRootAdmin) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800 text-sm flex items-center gap-2">
          <ShieldCheckIcon className="h-5 w-5" />
          Only Root Admins can manage branch ownership and admin access.
        </p>
      </div>
    );
  }

  if (adminLoading || branchLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-800 text-sm flex items-center gap-2">
            <CheckIcon className="h-5 w-5" />
            Changes saved successfully!
          </p>
        </div>
      )}

      {/* Branch Owner Section */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-gray-600" />
            Branch Owner
          </h3>
          {!isEditingOwner && (
            <button
              onClick={() => setIsEditingOwner(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <PencilIcon className="h-4 w-4" />
              Change Owner
            </button>
          )}
        </div>

        {isEditingOwner ? (
          <div className="space-y-4">
            <Dropdown
              label="Select New Owner"
              options={adminOptions}
              selected={
                adminOptions.find((opt) => opt._id === selectedOwnerId) || null
              }
              onChange={(selected) => setSelectedOwnerId(selected?._id || "")}
              placeholder="-- Select Admin --"
              required
              clearable
            />

            <div className="flex gap-2">
              <button
                onClick={handleUpdateOwner}
                disabled={branchLoading || !selectedOwnerId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {branchLoading ? "Updating..." : "Update Owner"}
              </button>
              <button
                onClick={() => {
                  setIsEditingOwner(false);
                  setSelectedOwnerId(branch?.ownerId || "");
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg">
            {currentOwner ? (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="font-medium">{currentOwner.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Email:</span>
                  <span className="font-medium">{currentOwner.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Role:</span>
                  <span className="font-medium capitalize">
                    {currentOwner.role}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Admin Code:</span>
                  <span className="font-mono text-sm">
                    {currentOwner.adminCode}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No owner assigned</p>
            )}
          </div>
        )}
      </div>

      {/* Admin Branch Access Section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <UserGroupIcon className="h-5 w-5 text-gray-600" />
          Admin Branch Access Management
        </h3>

        <div className="space-y-4">
          {customerAdmins.map((admin) => (
            <div
              key={admin._id}
              className="border rounded-lg p-4 hover:bg-gray-50"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{admin.name}</span>
                    <span className="text-sm text-gray-500">
                      ({admin.email})
                    </span>
                    {admin._id === branch?.ownerId && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        Owner
                      </span>
                    )}
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full capitalize">
                      {admin.role}
                    </span>
                  </div>

                  {editingAdminId === admin._id ? (
                    <div className="space-y-4 mt-4">
                      {/* Primary Branch Selection */}
                      <Dropdown
                        label="Primary Branch"
                        options={branchOptions}
                        selected={selectedPrimaryBranch}
                        onChange={setSelectedPrimaryBranch}
                        placeholder="Select primary branch"
                        required
                        clearable
                      />

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdatePrimaryBranch(admin._id)}
                          disabled={branchLoading}
                          className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                        >
                          {branchLoading ? "Updating..." : "Update Primary"}
                        </button>
                      </div>

                      {/* Secondary Branches Selection */}
                      <Dropdown
                        label="Secondary Branches"
                        options={branchOptions}
                        selected={selectedSecondaryBranches}
                        onChange={setSelectedSecondaryBranches}
                        placeholder="Select secondary branches"
                        multiple
                        clearable
                      />

                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            handleUpdateSecondaryBranches(admin._id)
                          }
                          disabled={branchLoading}
                          className="text-sm px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                        >
                          {branchLoading
                            ? "Updating..."
                            : "Update Secondary Branches"}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-sm px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm space-y-2 mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 font-medium">
                          Primary:
                        </span>
                        <span className="font-mono px-2 py-1 bg-blue-50 text-blue-700 rounded">
                          {admin.branchAccess?.primaryBranch || "Not set"}
                        </span>
                        {admin.branchAccess?.primaryBranch === branch?.code && (
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        )}
                      </div>

                      <div className="flex items-start gap-2">
                        <span className="text-gray-600 font-medium">
                          Secondary:
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {admin.branchAccess?.secondaryBranches?.length > 0 ? (
                            admin.branchAccess.secondaryBranches.map(
                              (code, idx) => (
                                <span
                                  key={idx}
                                  className={`px-2 py-1 text-xs rounded-full font-mono ${
                                    code === branch?.code
                                      ? "bg-green-100 text-green-700 font-semibold"
                                      : "bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  {code}
                                  {code === branch?.code && (
                                    <CheckIcon className="inline h-3 w-3 ml-1" />
                                  )}
                                </span>
                              )
                            )
                          ) : (
                            <span className="text-gray-500 italic">None</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {editingAdminId !== admin._id && (
                  <button
                    onClick={() => handleEditAdminAccess(admin)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 ml-4"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit Access
                  </button>
                )}
              </div>
            </div>
          ))}

          {customerAdmins.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No admins found for this customer
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BranchOwnershipManagement;
